"use client";

import { useRequestCart, useRequestSubscription } from "@packages/sdk";
import { useContext, useEffect, useState } from "react";

import { mixpanelContext } from "../lib/mixpanelContext";
import { HallowABFlag } from "../types/enums/HallowABFlag.enum";
import { MixpanelABFlagValidProps } from "../types/enums/MixpanelABFlagValidProps.enum";

export const useMixpanel = () => useContext(mixpanelContext);

export const useMixpanelFlag = ({
  key,
  targetedValue = "treatment",
  fallback = "excluded",
}: {
  key: HallowABFlag;
  targetedValue?: "control" | "treatment" | "excluded";
  fallback?: "control" | "treatment" | "excluded";
}) => {
  const isLogicLoading = useMixpanelLogic({ key });
  const { isLoading, getVariant } = useMixpanel();
  const [localLoading, setLocalLoading] = useState<boolean>(true);
  const [variant, setVariant] = useState<"control" | "treatment" | "excluded">(
    fallback,
  );

  useEffect(() => {
    if (!isLoading && !isLogicLoading) {
      getVariant(key, fallback).then((value) => {
        setVariant(value);
        setLocalLoading(false);
      });
    }
  }, [isLoading, isLogicLoading, key, fallback, getVariant]);

  return {
    isLoading: localLoading,
    isTargeted: variant === targetedValue,
    value: variant,
  };
};

export const useMixpanelLogic = ({ key }: { key: HallowABFlag }) => {
  const mixpanel = useMixpanel();
  const { query: subscription } = useRequestSubscription();
  const { query: cart } = useRequestCart({
    priceId: subscription.data?.price_id,
    promoCode: subscription.data?.promo,
    enabled: !!subscription.data?.price_id && !!subscription.data?.promo,
  });
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (mixpanel && key && subscription.data) {
      const validProp = MixpanelABFlagValidProps[key];

      if (key === HallowABFlag.FamilyPlanOneClickUpgrade) {
        let valid = true;
        // zero-day trials are out
        if (new Date(subscription.data.trial_ends_at) < new Date())
          valid = false;
        // discount promos are out
        if (cart.data?.discount) valid = false;
        mixpanel.updateContext({
          custom_properties: { [validProp]: valid ? "true" : "false" },
        });
        setLoading(false);
      }
    }
  }, [mixpanel, key, subscription.data]);

  return loading;
};
